import type * as React from "react";

import { cn } from "./cn";

/*
 * A white panel on the paper page: the card ring (`shadow-card`, never a
 * border, so nothing shifts), 12px corners. Never put a Card inside a Card —
 * inside one, group with `border-border` rules, spacing or a `bg-surface`
 * block without a ring.
 *
 * One spacing variable, `--card-spacing`: 24px, 16px for `size="sm"` or
 * inside `[data-density="compact"]`, 12px for a small card in the compact
 * tier. The card pads its top and bottom with it (a caller's `p-6` replaces
 * that); the sections pad their sides with it and put it between each other
 * as a top margin (`--card-gap`, zero on the first child). There is no flex
 * gap, so a caller's own `space-y-*` or a section's own `mt-*` is never added
 * on top of one.
 */
const cardBase = [
  "group/card relative flex flex-col overflow-hidden rounded-card bg-card py-(--card-spacing) text-sm text-card-foreground shadow-card",
  "[--card-spacing:--spacing(6)] data-[size=sm]:[--card-spacing:--spacing(4)]",
  "in-data-[density=compact]:[--card-spacing:--spacing(4)] in-data-[density=compact]:data-[size=sm]:[--card-spacing:--spacing(3)]",
  "[--card-gap:var(--card-spacing)] *:first:[--card-gap:0px]",
  "has-[>img:first-child]:pt-0 *:[img:first-child]:rounded-t-card *:[img:last-child]:rounded-b-card",
].join(" ");

/**
 * A card that is itself the link or button: its ring darkens on hover (no
 * lift, no drop), and the focus outline sits on the card.
 */
const cardInteractive =
  "cursor-pointer transition-[box-shadow] duration-150 ease-out outline-none hover:shadow-card-hover focus-visible:outline-2 focus-visible:outline-solid focus-visible:outline-offset-2 focus-visible:outline-ring";

type CardProps = React.ComponentProps<"div"> & {
  size?: "default" | "sm";
  interactive?: boolean;
};

function Card({
  className,
  size = "default",
  interactive = false,
  ...props
}: CardProps) {
  return (
    <div
      data-slot="card"
      data-size={size}
      className={cn(cardBase, interactive && cardInteractive, className)}
      {...props}
    />
  );
}

function CardHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-header"
      className={cn(
        "group/card-header @container/card-header mt-(--card-gap) grid auto-rows-min items-start gap-1.5 px-(--card-spacing) @md/card-header:has-data-[slot=card-action]:grid-cols-[1fr_auto] has-data-[slot=card-description]:grid-rows-[auto_auto] [.border-b]:pb-(--card-spacing)",
        className,
      )}
      {...props}
    />
  );
}

/** The card's title: the `title` type role (20px, 16px compact), 600, the ink. */
function CardTitle({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-title"
      className={cn(
        "type-title text-foreground group-data-[size=sm]/card:text-base",
        className,
      )}
      {...props}
    />
  );
}

function CardDescription({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-description"
      className={cn("text-sm text-muted-foreground", className)}
      {...props}
    />
  );
}

function CardAction({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-action"
      className={cn(
        // Beside the title when the header is wide enough; under it on a
        // narrow card, so wide controls never cover the title.
        "col-start-1 max-w-full min-w-0 self-start justify-self-start @md/card-header:col-start-2 @md/card-header:row-span-2 @md/card-header:row-start-1 @md/card-header:justify-self-end",
        className,
      )}
      {...props}
    />
  );
}

function CardContent({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-content"
      className={cn("mt-(--card-gap) px-(--card-spacing)", className)}
      {...props}
    />
  );
}

function CardFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="card-footer"
      className={cn(
        "mt-(--card-gap) flex items-center px-(--card-spacing) [.border-t]:pt-(--card-spacing)",
        className,
      )}
      {...props}
    />
  );
}

export {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
};
export type { CardProps };
